import { FC, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import gsap from 'gsap'
import Swiper from 'swiper'

import css from './PageWork.module.scss'

import { WorkCopyType } from '@/data/fixed-pages/copy-work'
import routes from '@/data/routes'
import { PageHandle, PageProps } from '@/data/types'

import { updateCustomProperties } from '@/utils/basic-functions'
import { MOTION } from '@/utils/motion-values'
import sass from '@/utils/sass'

import useLayout from '@/hooks/use-layout'
import { useRefs } from '@/hooks/use-refs'

import BaseImage from '@/components/BaseImage/BaseImage'
import CarouselControls from '@/components/CarouselControls/CarouselControls'
import CarouselCounter from '@/components/CarouselCounter/CarouselCounter'
import CtaLink from '@/components/CtaLink/CtaLink'
import CustomCursorWork from '@/components/CustomCursorWork/CustomCursorWork'
import CustomCursorWorkHover from '@/components/CustomCursorWorkHover/CustomCursorWorkHover'
import Heading from '@/components/Heading/Heading'
import Label from '@/components/Label/Label'
import Paragraph from '@/components/Paragraph/Paragraph'
import PillButton from '@/components/PillButton/PillButton'

export interface PageWorkProps extends PageProps {
  className?: string
  content: WorkCopyType
}

export type ViewRefs = {
  root: HTMLDivElement
  withLabelRef: HTMLDivElement
  roleLabelRef: HTMLDivElement
  typeLabelRef: HTMLDivElement
  backgrounds: HTMLDivElement
  leftSwiperContainerElRef: HTMLDivElement
  leftSwiperWrapperRef: HTMLDivElement
  rightSwiperContainerElRef: HTMLDivElement
  rightSwiperWrapperRef: HTMLDivElement
  withLabelHeadingRef: HTMLParagraphElement
  roleLabelHeadingRef: HTMLParagraphElement
  typeLabelHeadingRef: HTMLParagraphElement
  animateOutPanel1: HTMLDivElement
  animateOutPanel2: HTMLDivElement
}

const View: FC<PageWorkProps> = ({ className, content, onReady }) => {
  const viewRefs = useRefs<ViewRefs>()
  const handleRef = useRef<PageHandle>(null)
  const layout = useLayout()
  const [progress, setProgress] = useState(0)

  const router = useRouter()
  const [leftSlideTransitioning, setLeftSlideTransitioning] = useState(false)
  const [rightSlideTransitioning, setRightSlideTransitioning] = useState(false)

  const leftSwiperObject = useRef<Swiper | null>(null)
  const rightSwiperObject = useRef<Swiper | null>(null)
  const [leftSwiperInit, setLeftSwiperInit] = useState(false)
  const [rightSwiperInit, setRightSwiperInit] = useState(false)

  const nextIndex = useMemo(() => (progress + 1) % content.projects.length, [progress, content.projects.length])
  const prevIndex = useMemo(() => (progress - 1) % content.projects.length, [progress, content.projects.length])

  const [withLabel, setWithLabel] = useState('')
  const [roleLabel, setRoleLabel] = useState('')
  const [typeLabel, setTypeLabel] = useState('')

  const animateDone = useRef(false)

  const activeProject = content.projects[progress].style.css
  const zIndexBgCounter = useRef(1)

  useEffect(() => {
    let readyTimeout: NodeJS.Timeout

    gsap.set(viewRefs.root.current, { opacity: 0 })

    if (leftSwiperInit && rightSwiperInit) {
      readyTimeout = setTimeout(() => {
        onReady?.(handleRef)
      }, MOTION.general.delayForLayout)
    }

    return () => {
      if (readyTimeout) clearTimeout(readyTimeout)
    }
  }, [onReady, leftSwiperInit, rightSwiperInit, viewRefs])

  useEffect(() => {
    const onBeforeHistoryChange = (e: string) => {
      const colorChangingPanel = viewRefs.animateOutPanel2.current

      switch (e) {
        case routes.Home.path:
        case routes.Photo.path:
        case routes.Gallery.path:
        case routes.Work.path:
        case routes.NotFound.path:
          gsap.set(colorChangingPanel, {
            backgroundColor: sass.white
          })

          break
        default: {
          if (e.includes(routes.Work.path)) {
            gsap.set(colorChangingPanel, {
              backgroundColor: sass.black
            })
          }
          break
        }
      }
    }

    router.events.on('beforeHistoryChange', onBeforeHistoryChange)
    return () => {
      router.events.off('beforeHistoryChange', onBeforeHistoryChange)
    }
  }, [router, viewRefs])

  useImperativeHandle(handleRef, () => ({
    animateIn: () => {
      const timeline = gsap.timeline().set(viewRefs.root.current, { opacity: 1 }, 0)

      const wrapper = viewRefs.leftSwiperWrapperRef.current
      const firstSlide = wrapper?.querySelector('.swiper-slide-active')
      const firstImage = firstSlide?.querySelector('img')

      if (!firstSlide || !firstImage) {
        return timeline
      }

      gsap.set(firstImage, { scale: 1.2 })

      if (layout.desktop) {
        gsap.set(firstSlide, { yPercent: 120 })
      } else {
        gsap.set(firstSlide, { xPercent: 120 })
      }

      timeline.to(
        firstSlide,
        {
          yPercent: 0,
          xPercent: 0,
          duration:
            (layout.desktop
              ? MOTION.general.carousel.carouselTransitionInSec
              : MOTION.general.carousel.carouselMobileTransitionInSec) - 0.1,
          overwrite: true,
          ease: 'css-ease-out', // to match ease-out of the swiper
          onComplete: () => {
            animateDone.current = true
          }
        },
        '>0.334'
      )

      // animate first image
      timeline.to(
        firstImage,
        {
          scale: 1,
          duration: 1.2,
          ease: 'ease-scale'
        },
        '<'
      )

      // animate first letters
      if (rightSwiperObject.current) {
        const currentSlide = rightSwiperObject.current.slides[rightSwiperObject.current.activeIndex]
        const currentAllHeadings = Array.from(currentSlide.querySelectorAll('.Heading'))

        timeline.set(currentSlide, { opacity: 1, ease: 'ease-opacity', zIndex: 1 }, 0)

        currentAllHeadings.forEach((heading, i) => {
          let delay
          if (i === 0) delay = '>+0.5'
          else delay = `<+0.1`

          timeline.textRiseByCharsIn(
            heading,
            {
              duration: 1.5,
              charDuration: 1.5,
              charOffset: 0.075,
              ease: 'ease-split-text'
            },
            delay
          )
        })
      }

      timeline.fromTo(
        [
          viewRefs.withLabelHeadingRef.current,
          viewRefs.roleLabelHeadingRef.current,
          viewRefs.typeLabelHeadingRef.current
        ],
        {
          opacity: 0
        },
        {
          opacity: 1,
          duration: 0.8,
          stagger: 0.12
        },
        '<-0.1'
      )

      return timeline
    },
    animateOut: () => {
      const duration = MOTION.general.wipeLayers.duration

      return gsap
        .timeline()
        .to(viewRefs.animateOutPanel1.current, { yPercent: 0, duration, ease: 'ease-x-y-percent' }, 0)
        .to(viewRefs.animateOutPanel1.current, { yPercent: -100, duration, ease: 'ease-x-y-percent' }, duration)
        .to(viewRefs.animateOutPanel2.current, { yPercent: 0, duration, ease: 'ease-x-y-percent' }, duration)
        .to(viewRefs.animateOutPanel2.current, { yPercent: -100, duration, ease: 'ease-x-y-percent' }, duration * 2)
        .set(viewRefs.root.current, { opacity: 0 }, '>')
    }
  }))

  const handleSlideScaleTransition = useCallback(
    (swiper: Swiper) => {
      setProgress(swiper?.realIndex)

      const currentSlide = swiper.slides[swiper.activeIndex]
      const image = currentSlide.querySelector('img')

      if (!layout.desktop || !animateDone.current || !image) {
        return
      }

      gsap.fromTo(
        image,
        {
          scale: 1.2
        },
        {
          scale: 1,
          duration: 1.2,
          ease: 'ease-scale'
        }
      )
    },

    [layout.desktop]
  )

  const handleLettersSlideChange = useCallback((swiper: Swiper, delay = 0) => {
    const currentSlide = swiper.slides[swiper.activeIndex]
    const restSlides = swiper.slides.filter((_, i) => i !== swiper.activeIndex)
    const allHeadings = restSlides.map((slide) => Array.from(slide.querySelectorAll('.Heading'))).flat()

    const currentAllHeadings = Array.from(currentSlide.querySelectorAll('.Heading'))

    const timeline = gsap
      .timeline({ delay })
      .to(allHeadings, { opacity: 0, duration: 0.667, ease: 'ease-opacity' }, 0)
      .set(restSlides, { opacity: 0, zIndex: -1 }, 0.4)
      .set(currentSlide, { opacity: 1, zIndex: 1 }, 0.4)

    currentAllHeadings.forEach((heading, i) => {
      timeline.textRiseByCharsIn(
        heading,
        {
          duration: 1.5,
          charDuration: 1.5,
          charOffset: 0.075,
          ease: 'ease-split-text'
        },
        0.5 + i * 0.1
      )
    })
  }, [])

  const moveCarouselsForward = useCallback(() => {
    if (leftSlideTransitioning || rightSlideTransitioning) return

    leftSwiperObject.current?.slideNext()
    rightSwiperObject.current?.slideNext()
  }, [leftSlideTransitioning, rightSlideTransitioning])

  const moveCarouselsBackward = useCallback(() => {
    if (leftSlideTransitioning || rightSlideTransitioning) return

    leftSwiperObject.current?.slidePrev()
    rightSwiperObject.current?.slidePrev()
  }, [leftSlideTransitioning, rightSlideTransitioning])

  useEffect(() => {
    // Detect arrow left and right key presses
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        moveCarouselsBackward()
      } else if (e.key === 'ArrowRight') {
        moveCarouselsForward()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [moveCarouselsBackward, moveCarouselsForward])

  useEffect(() => {
    const initialSlide = 0
    const swiperTransitionDuration = layout.desktop
      ? MOTION.general.carousel.carouselTransitionInMs
      : MOTION.general.carousel.carouselMobileTransitionInMs // ms

    // [LEFT] Image
    if (viewRefs.leftSwiperContainerElRef.current) {
      leftSwiperObject.current = new Swiper(viewRefs.leftSwiperContainerElRef.current, {
        slidesPerView: 1,
        spaceBetween: 0,
        wrapperClass: css.leftGalleryWrapper,
        slideClass: css.leftGalleryItem,
        initialSlide,
        loop: true,
        speed: swiperTransitionDuration, // ms
        allowTouchMove: layout.desktop ? false : true,
        on: {
          afterInit: () => {
            setLeftSwiperInit(true)
          },
          slideChangeTransitionStart: (swiper) => {
            handleSlideScaleTransition(swiper)
            setLeftSlideTransitioning(true)
          },
          slideChangeTransitionEnd: () => {
            setLeftSlideTransitioning(false)
          }
        },
        direction: layout.desktop ? 'vertical' : 'horizontal'
      })
    }

    // [RIGHT] Letters
    if (viewRefs.rightSwiperContainerElRef.current) {
      rightSwiperObject.current = new Swiper(viewRefs.rightSwiperContainerElRef.current, {
        slidesPerView: 1,
        spaceBetween: 0,
        wrapperClass: css.rightGalleryWrapper,
        slideClass: css.rightGalleryItem,
        allowTouchMove: false,
        speed: 0,
        initialSlide,
        on: {
          afterInit: () => {
            setRightSwiperInit(true)
          },
          slideChangeTransitionStart: (swiper) => {
            handleLettersSlideChange(swiper)
            setRightSlideTransitioning(true)
          },
          slideChangeTransitionEnd: () => {
            setRightSlideTransitioning(false)
          }
        }
      })
    }

    return () => {
      leftSwiperObject.current?.destroy()
      leftSwiperObject.current = null
      rightSwiperObject.current?.destroy()
      rightSwiperObject.current = null
    }
  }, [layout.desktop, handleLettersSlideChange, handleSlideScaleTransition, viewRefs])

  useEffect(() => {
    if (!onReady) return

    const currentProject = content.projects[progress].title

    rightSwiperObject.current?.slideTo(progress, 0)

    if (currentProject === 'Pixel Arena') {
      updateCustomProperties({
        backButtonColor: sass.white,
        navButtonBackground: sass.white,
        navButtonColor: sass.black,
        navButtonBorder: 'transparent'
      })
    } else {
      updateCustomProperties({
        backButtonColor: sass.black,
        navButtonBackground: sass.black,
        navButtonColor: sass.white,
        navButtonBorder: 'transparent'
      })
    }

    updateCustomProperties({
      backButtonOpacity: '1',
      backButtonVisibility: 'visible'
    })

    if (layout.desktop) {
      updateCustomProperties({
        backButtonColor: sass.white
      })
    }

    gsap
      .timeline()
      .to(
        [viewRefs.withLabelRef.current, viewRefs.roleLabelRef.current, viewRefs.typeLabelRef.current],
        {
          opacity: 0,
          duration: 0.8,
          ease: 'ease-opacity',
          onComplete: () => {
            setWithLabel(content.projects[progress].about.with)
            setRoleLabel(content.projects[progress].about.role)
            setTypeLabel(content.projects[progress].about.type)
          }
        },
        0
      )
      .to(
        [viewRefs.withLabelRef.current, viewRefs.roleLabelRef.current, viewRefs.typeLabelRef.current],
        {
          opacity: 1,
          duration: 0.8,
          stagger: 0.12,
          ease: 'ease-opacity'
        },
        '>=+0.1'
      )

    const currentProjectEl = viewRefs.backgrounds.current?.childNodes[progress]

    if (!currentProjectEl) return

    gsap.set(currentProjectEl, { zIndex: zIndexBgCounter.current })

    if (layout.desktop) {
      gsap.set(currentProjectEl, { xPercent: 0, overwrite: true })

      gsap.fromTo(
        currentProjectEl,
        { yPercent: 100 },
        { yPercent: 0, duration: 1, ease: 'ease-x-y-percent', overwrite: true }
      )
    } else {
      gsap.set(currentProjectEl, { yPercent: 0, overwrite: true })
      gsap.fromTo(
        currentProjectEl,
        { xPercent: 100 },
        {
          xPercent: 0,
          duration: layout.desktop
            ? MOTION.general.carousel.carouselTransitionInSec
            : MOTION.general.carousel.carouselMobileTransitionInSec,
          ease: 'css-ease-out',
          overwrite: true,
          delay: 0.1
        }
      )
    }

    zIndexBgCounter.current += 1
  }, [layout, progress, content.projects, viewRefs, onReady])

  return (
    <div className={classNames('PageWork', css.root, className, css[activeProject])} ref={viewRefs.root}>
      <div className={css.backgroundProjectLayer} ref={viewRefs.backgrounds}>
        {content.projects.map(({ style }, i) => (
          <div className={classNames(css.backgroundProjectItem, css[style.css])} key={i} />
        ))}
      </div>

      <div className={css.leftGallery} ref={viewRefs.leftSwiperContainerElRef}>
        <div className={css.leftGalleryWrapper} ref={viewRefs.leftSwiperWrapperRef}>
          {content.projects.map(({ image, route, style }, i) => (
            <div className={classNames(css.leftGalleryItem, 'CursorLeftWork')} key={i}>
              <Link
                className={classNames(css.leftGalleryLink, 'CursorLeftWork')}
                href={route.path}
                onClick={(event) => {
                  if (!layout.device) event.preventDefault()
                }}
                scroll={false}
              >
                <BaseImage
                  className={classNames(css.galleryImage, css[style.coverPosition])}
                  data={image}
                  fill={false}
                  loading={i === nextIndex || i === prevIndex ? 'eager' : 'lazy'}
                  priority={i === nextIndex || i === prevIndex ? true : false}
                />
              </Link>
            </div>
          ))}
        </div>
      </div>

      {layout.device ? null : (
        <>
          <CustomCursorWork handleClickLeft={moveCarouselsBackward} handleClickRight={moveCarouselsForward} />
          <CustomCursorWorkHover text={content.pills.seeProject} />
        </>
      )}

      <div className={css.description}>
        <Paragraph paragraphType={2} italic ref={viewRefs.withLabelHeadingRef}>
          With
        </Paragraph>
        <Label labelType={3} ref={viewRefs.withLabelRef}>
          {withLabel}
        </Label>

        <Paragraph paragraphType={2} italic ref={viewRefs.roleLabelHeadingRef}>
          Role
        </Paragraph>
        <Label labelType={3} ref={viewRefs.roleLabelRef}>
          {roleLabel}
        </Label>

        <Paragraph paragraphType={2} italic ref={viewRefs.typeLabelHeadingRef}>
          Type
        </Paragraph>
        <Label labelType={3} ref={viewRefs.typeLabelRef}>
          {typeLabel}
        </Label>
      </div>

      <div className={css.rightGallery} ref={viewRefs.rightSwiperContainerElRef}>
        <div className={classNames(css.rightGalleryWrapper, 'CursorRightWork')} ref={viewRefs.rightSwiperWrapperRef}>
          {content.projects.map(({ title, style, route }, i) => {
            const words = layout.desktop && style.splitBySpaces ? title.split(' ') : [title]

            return (
              <div className={classNames(css.rightGalleryItem, 'CursorRightWork')} key={i} data-project={title}>
                <CtaLink
                  className={classNames(css.link, 'WorkHoverHeading', `theme-${style.buttonColor}`)}
                  href={route.path}
                  withOpacityHover
                >
                  {words.map((word) => {
                    return (
                      <Heading
                        className={classNames(css.heading, 'WorkHoverHeading', `theme-${style.buttonColor}`)}
                        key={`${route}${word}`}
                        as="h1"
                        uppercase
                      >
                        {word}
                      </Heading>
                    )
                  })}
                </CtaLink>
              </div>
            )
          })}
        </div>
      </div>

      <CarouselCounter className={css.counter} current={progress + 1} total={content.projects.length} />

      <PillButton className={css.seeMore} theme={content.projects[progress].style.buttonColor}>
        <Link href={content.projects[progress].route.path} scroll={false}>
          {content.pills.seeProject}
        </Link>
      </PillButton>

      <CarouselControls
        className={css.controls}
        backCallback={moveCarouselsBackward}
        forwardCallback={moveCarouselsForward}
      />

      <div className={css.animateOutContainer}>
        <div
          className={classNames(css.panel1, css[content.projects[progress].style.css])}
          ref={viewRefs.animateOutPanel1}
        />
        <div className={css.panel2} ref={viewRefs.animateOutPanel2} />
      </div>
    </div>
  )
}

View.displayName = 'PageWork-View'

// Controller (handles global state, router, data fetching, etc. Feeds props to the view component)
const PageWork: FC<PageWorkProps> = (props) => {
  return <View {...props} />
}

PageWork.displayName = 'PageWork'

export default memo(PageWork)
